// ===============================================================
// ⭐ ChangePhonePage.js — Change phone number (i18n + TopBar + LivoraToast)
// ===============================================================

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ChangePhonePage.css";

import TopBar from "../components/common/TopBar";
import { useTranslation } from "react-i18next";
import useLivoraToast from "../hooks/useLivoraToast";
import LivoraToast from "../components/common/LivoraToast";

// Firebase
import {
  RecaptchaVerifier,
  PhoneAuthProvider,
  updatePhoneNumber,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, updateDoc } from "firebase/firestore";
import { normalizeDigits } from "../utils/digits";

function ChangePhonePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // ✅ Livora Toast
  const { toast, showToast, hideToast } = useLivoraToast();

  const currentPhone =
    auth.currentUser?.phoneNumber ||
    t("not_linked", { defaultValue: "غير مرتبط" });

  // -------------------------------------------------------------
  // reCAPTCHA (once)
  // -------------------------------------------------------------
  const getRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        { size: "invisible" }
      );
    }
    return window.recaptchaVerifier;
  };

  // -------------------------------------------------------------
  // Send SMS code for phone update
  // -------------------------------------------------------------
  const sendCode = async (e) => {
    e.preventDefault();

    if (!phone || !phone.startsWith("+")) {
      showToast(
        "error",
        t("phone_format_error", {
          defaultValue: "الرجاء إدخال الرقم بصيغة صحيحة مثل +43...",
        })
      );
      return;
    }

    if (loading) return;

    try {
      setLoading(true);

      const user = auth.currentUser;
      if (!user) {
        showToast(
          "error",
          t("general_error", {
            defaultValue: "حدث خطأ عام",
          })
        );
        return;
      }

      const appVerifier = getRecaptcha();
      const provider = new PhoneAuthProvider(auth);

      const id = await provider.verifyPhoneNumber(phone.trim(), appVerifier);

      setVerificationId(id);
      setStep(2);

      showToast(
        "success",
        t("sms_sent", {
          defaultValue: "تم إرسال رمز التحقق بنجاح",
        })
      );
    } catch (err) {
      console.error(err);
      showToast(
        "error",
        t("sms_send_error", {
          defaultValue: "فشل إرسال رمز التحقق",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------
  // Confirm code and update current user's phone
  // -------------------------------------------------------------
  const confirmCode = async (e) => {
    e.preventDefault();

    if (!code || code.trim().length < 4) {
      showToast(
        "error",
        t("enter_valid_code", {
          defaultValue: "الرجاء إدخال رمز تحقق صحيح",
        })
      );
      return;
    }

    if (!verificationId) {
      showToast(
        "error",
        t("code_not_sent", {
          defaultValue: "لم يتم إرسال الرمز بعد",
        })
      );
      return;
    }

    try {
      setLoading(true);

      const user = auth.currentUser;
      if (!user) {
        showToast(
          "error",
          t("general_error", {
            defaultValue: "حدث خطأ عام",
          })
        );
        return;
      }

      const credential = PhoneAuthProvider.credential(
        verificationId,
        code.trim()
      );

      await updatePhoneNumber(user, credential);

      await updateDoc(doc(db, "users", user.uid), {
        phoneNumber: phone.trim(),
      });

      showToast(
        "success",
        t("phone_update_success", {
          defaultValue: "تم تحديث رقم الهاتف بنجاح",
        })
      );

      setPhone("");
      setCode("");
      setVerificationId("");

      setTimeout(() => {
        navigate("/settings");
      }, 500);
    } catch (err) {
      console.error(err);

      if (err.code === "auth/invalid-verification-code") {
        showToast(
          "error",
          t("code_invalid", {
            defaultValue: "رمز التحقق غير صحيح",
          })
        );
      } else if (err.code === "auth/code-expired") {
        showToast(
          "error",
          t("code_expired", {
            defaultValue: "انتهت صلاحية الرمز، أعد المحاولة",
          })
        );
      } else if (err.code === "auth/requires-recent-login") {
        showToast(
          "error",
          t("requires_recent_login", {
            defaultValue: "يجب تسجيل الدخول مجددًا قبل تنفيذ هذا الإجراء",
          })
        );
      } else {
        showToast(
          "error",
          t("general_error", {
            defaultValue: "حدث خطأ عام",
          })
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setCode("");
      setVerificationId("");
    } else {
      navigate(-1);
    }
  };

  // -------------------------------------------------------------
  return (
    <div className="change-phone-page">
      <div id="recaptcha-container" style={{ display: "none" }} />

      <TopBar
        title={t("change_phone", { defaultValue: "تغيير رقم الهاتف" })}
        onBack={handleBack}
      />

      <div className="page-content">
        {step === 1 && (
          <form onSubmit={sendCode}>
            <div className="current-phone-box">
              <div className="current-phone-label">
                {t("current_phone", {
                  defaultValue: "رقم الهاتف الحالي",
                })}
              </div>
              <div className="current-phone-value">{currentPhone}</div>
            </div>

            <input
              type="tel"
              className="input-field"
              placeholder={t("phone_placeholder", {
                defaultValue: "+43...",
              })}
              value={phone}
              onChange={(e) => setPhone(normalizeDigits(e.target.value))}
              autoComplete="tel"
            />

            <button className="main-btn" type="submit" disabled={loading}>
              {loading
                ? t("sending_code", { defaultValue: "جارٍ إرسال الكود..." })
                : t("send_code", { defaultValue: "إرسال الكود" })}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={confirmCode}>
            <p className="hint-text">
              {t("code_sent_to", {
                defaultValue: "تم إرسال الرمز إلى",
              })}
              <br />
              <strong>{phone}</strong>
            </p>

            <input
              type="text"
              className="input-field"
              placeholder={t("enter_code", {
                defaultValue: "أدخل رمز التحقق",
              })}
              value={code}
              onChange={(e) => setCode(normalizeDigits(e.target.value))}
              autoComplete="one-time-code"
              inputMode="numeric"
            />

            <button className="main-btn" type="submit" disabled={loading}>
              {loading
                ? t("confirming", { defaultValue: "جارٍ التأكيد..." })
                : t("confirm_phone", { defaultValue: "تأكيد الرقم" })}
            </button>

            <button
              className="secondary-btn"
              type="button"
              onClick={() => {
                setStep(1);
                setCode("");
                setVerificationId("");
              }}
              disabled={loading}
            >
              {t("edit_phone", { defaultValue: "تعديل الرقم" })}
            </button>
          </form>
        )}
      </div>

      {/* ✅ Livora Toast */}
      <LivoraToast toast={toast} onClose={hideToast} />
    </div>
  );
}

export default ChangePhonePage;